import BasicLayout from "@container/layout/basic";
import { useEffect, useState } from "react";
import CustomVoiceInterviewV2 from "@container/custom-voice-interview-v2";
import { useNavigate, useParams } from "react-router-dom";
import { useUserProfileContext } from "@context/user_profile";
import { formatCV } from "@util/string";
import { CVData } from "@type/cv";
import { getMyCV } from "@service/cv";
import { catchErrorLog } from "@util/sentry";
import { getFirstName } from "@util/name";
import { InterviewAssessment } from "@type/assessments";
import { CustomInterviewSection } from "@type/videoInterview";
import { getInterviewStatus } from "@service/interview";
import { getAssessment } from "@service/assessment";
import { DailyProvider, useCallObject } from "@daily-co/daily-react";
import { DailyCall } from "@daily-co/daily-js";
import { SpeechDetectionProvider } from "@context/speech_detection_context";
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "@component/card";
import { ArrowRightIcon, CheckIcon } from "lucide-react";
import { Button } from "@component/button";

export default function PracticeInterview() {
    const { interviewid } = useParams() || null;
    const navigate = useNavigate();
    // const snackbar = useSnackbar();
    const [isLoadingUserProfile, userInfo] = useUserProfileContext();
    const [, setInterviewStatus] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [cvData, setCVData] = useState<CVData | null>(null);
    const [interviewStage, setInterviewStage] = useState<string>("interview");
    const [assessment, setAssessment] = useState<InterviewAssessment | null>(null);
    const [formattedSections, setFormattedSections] = useState<CustomInterviewSection[]>([]);
    const [isLoadingInterviewDetails, setIsLoadingInterviewDetails] = useState<boolean>(true);

    const callObject = useCallObject({}) as DailyCall;

    const getCandidateName = () => {
        if (!cvData) return "Anonymous Candidate";
        const cv = cvData
        return cv?.name || "Anonymous Candidate";
    };

    const INTERVIEW_CONFIG = {
        conversationName: `${getCandidateName()}: Screening Interview (${new Date().toISOString()})`,
        conversationalContext: `Never ask the user about a 'position', this is a general screening interview. \n\nCandidate CV/Resume:\n${formatCV(cvData)}`,
    };

    const REPLICA_CONFIG = {
        label: import.meta.env.VITE_TAVUS_REPLICA_LABEL,
        replicaId: import.meta.env.VITE_TAVUS_REPLICA_ID,
        personaId: import.meta.env.VITE_TAVUS_PERSONA_ID,
        greeting: import.meta.env.VITE_TAVUS_GREETING,
    };

    const fetchAssessmentData = async () => {
        if (!interviewid) {
            setIsLoadingInterviewDetails(false);
            return;
        }

        setIsLoadingInterviewDetails(true);

        try {
            const response = await getAssessment(interviewid as string);
            if (response) {
                const assessmentData = response as InterviewAssessment;
                setAssessment(assessmentData);
                // Format sections for the test interview
                const sections = assessmentData?.content.sections.map((section: any) => ({
                    title: section.title,
                    instruction: section.instruction,
                    duration: section.duration,
                    questions: section.questions?.map((q: any) => q.text).filter((text: string) => text?.trim() !== "") || undefined,
                    transition: section.transition || undefined
                }));
                setFormattedSections(sections as CustomInterviewSection[]);
            }
        } catch (err) {
            catchErrorLog(`Error fetching assessment: ${err}`);
        } finally {
            setIsLoadingInterviewDetails(false);
        }
    }

    useEffect(() => {
        if (!isLoadingUserProfile && userInfo && interviewid) {
            getMyCV()
                .then((res) => {
                    if (res.code === 200 && res.data) {
                        setCVData(res.data.CV_data);
                    } else {
                        catchErrorLog(`Error fetching CV data: ${res.message}`);
                    }
                })
                .catch((err) => {
                    catchErrorLog(`Error fetching CV data: ${err}`);
                });

            getInterviewStatus(interviewid).then((res) => {
                console.log("Interview Status Check Response:", res);
                setInterviewStatus(res?.data?.status || null);
            }).then(() => {
                setIsLoading(false);
            })

            fetchAssessmentData()
        }
    }, [isLoadingUserProfile, userInfo, interviewid]);

    return (
        <BasicLayout>
            <div className="container max-w-6xl mx-auto sm:px-4 sm:py-8 flex flex-col min-h-[510px] items-center justify-center">
                <div className="container max-w-6xl mx-auto px-4 sm:px-0 py-4 sm:py-0 flex flex-col min-h-[510px] items-center justify-center sm:mb-0">
                    {(interviewStage === "interview" && !isLoading && !isLoadingInterviewDetails) && (
                        <DailyProvider callObject={callObject}>
                            <SpeechDetectionProvider
                                volumeThreshold={30}
                                silenceDelay={400}
                                autoStart={true}
                            >
                                <CustomVoiceInterviewV2
                                    interviewId={interviewid as string}
                                    pipecatApiUrl={import.meta.env.VITE_PIPECAT_API_URL || ""}
                                    agentName={import.meta.env.VITE_PIPECAT_CUSTOM_AGENT_NAME || ""}
                                    authToken={import.meta.env.VITE_PIPECAT_AUTH_TOKEN || ""}
                                    s3BucketName={import.meta.env.VITE_S3_BUCKET_NAME || ""}
                                    s3BucketRegion={import.meta.env.VITE_S3_BUCKET_REGION || ""}
                                    awsAssumeRoleArn={import.meta.env.VITE_AWS_ASSUME_ROLE_ARN || ""}
                                    fastapiURL={import.meta.env.VITE_TALENT_AI_BE_BASE_URL || ""}
                                    userId={userInfo?.id || ""}
                                    userCV={formatCV(cvData)}
                                    userName={getFirstName(cvData?.name as string) || "Test User"}
                                    isSkipInterview={false}
                                    isLoadingSkipInterview={false}
                                    sections={formattedSections as CustomInterviewSection[]}
                                    interviewName={assessment?.content.interviewName || "Test Interview"}
                                    description={assessment?.content.description || "This is a test interview."}
                                    maxDuration={assessment?.content.maxDuration || 20}
                                    interviewContext={assessment?.content.interviewContext || INTERVIEW_CONFIG.conversationalContext}
                                    closingStatement={assessment?.content.closingStatement || ""}
                                    interviewerPersona={assessment?.content.interviewerPersona || ""}
                                    toneAndCommunication={assessment?.content.toneAndCommunication || undefined}
                                    interviewFlowPrinciples={assessment?.content.interviewFlowPrinciples || undefined}
                                    informationToShare={assessment?.content.informationToShare || undefined}
                                    customGreeting={assessment?.content.customGreeting || REPLICA_CONFIG.greeting}
                                    aiName={assessment?.content.aiName || REPLICA_CONFIG.label}
                                    interviewLanguage={assessment?.content.interviewLanguage || "en-US"}
                                    voice={assessment?.content.voice || "male"}
                                    idleTimeoutMessage={assessment?.content.idleTimeoutMessage}
                                    isPracticeInterview={true}
                                    hasFormAssessment={false}
                                    hasEligibilityCheck={false}
                                    onCloseModal={() => {
                                        setInterviewStage("complete");
                                    }}
                                    onCompletedInterview={() => {
                                        console.log("Practice interview completed callback triggered");
                                    }}
                                />
                            </SpeechDetectionProvider>
                        </DailyProvider>
                    )}

                    {(interviewStage === "complete") && (
                        <>
                            <Card>
                                <CardHeader>
                                    <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <CheckIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                                    </div>
                                    <CardTitle className="text-center text-2xl">
                                        Practice Interview Complete!
                                    </CardTitle>
                                    <CardDescription className="text-center">
                                        Thank you for completing your {assessment?.content.interviewName} practice interview with <strong>ourteam AI.</strong>
                                        <br />
                                        Your insights are now being generated and will be available in 5-10 minutes.
                                    </CardDescription>
                                </CardHeader>
                                <CardFooter>
                                    <Button
                                        onClick={() => { navigate(`/interview`) }}
                                        className="w-full group"
                                    >
                                        View Your Interviews
                                        <ArrowRightIcon className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                                    </Button>
                                </CardFooter>
                            </Card>
                        </>
                    )}
                </div>
            </div>
        </BasicLayout>
    )
}